/**
 * Created by piotr.pozniak@thebeaverhead.com on 11/04/2023.
 */

import React, { useCallback, useMemo } from "react";
import Editor from "./Editor";
import { AvailableRulesOptionsContext, FiltersContext } from "./contexts";
import AsyncSelection from "../WidgetSettings/AsyncSelection";
import AxiosConfig from "../../../../AxiosConfig";

const exampleFilter = {
  conjunction: "and",
  groups: [
    {
      conjunction: "and",
      rules: [
        {
          field: {
            value: "tag",
            label: "Tag",
          },
          value: "",
          label: "Test",
          operator: {
            value: "is",
            label: "is",
          },
        },
      ],
    },
  ],
};

const asyncSelectionExample = {
  component: AsyncSelection,
  props: {
    baseUrl:
      AxiosConfig.getEndpointAddress() +
      `/api/filters/calendar_filters/5613aadf-4797-418f-9f85-bfc81c877baa.json?type=all_filters&kind=group&q=`,
    placeholder: "All Group",
    noOptionsMessage: "Start typing to search Group",
  },
};

const exampleAvailableRules = [
  {
    field: {
      value: "tag",
      label: "Tag",
    },
    operators: [
      {
        label: "is",
        value: "is",
        input: asyncSelectionExample,
      },
      {
        label: "is not",
        value: "not",
        input: {
          component: AsyncSelection,
          props: {
            url: "/api/v1/groups",
          },
        },
      },
    ],
  },
];

/**
 * @typedef {Object} Field
 * @property {string} value - The internal value of the field.
 * @property {string} label - The display label of the field.
 */

/**
 * @typedef {Object} InputConfig
 * @property {Function} component - The input component used for this operator.
 * @property {Object} [props] - Optional props passed to the input component.
 */

/**
 * @typedef {Object} Operator
 * @property {string} label - The display label of the operator.
 * @property {string} value - The internal value of the operator.
 * @property {InputConfig} [input] - Optional input configuration for this operator.
 */

/**
 * @typedef {Object} AvailableFilter
 * @property {Field} field - The field this filter applies to.
 * @property {Operator[]} operators - The list of operators available for this filter.
 */

/**
 * @typedef {Object} FiltersProps
 * @property {AvailableFilter[]} availableFilters - The list of filters that can be applied.
 * @property {Function} onChange - Callback fired when the filter configuration changes.
 */

/**
 *
 * @param {FiltersProps} props
 * @returns {React.JSX.Element}
 * @constructor
 */
const Filters = (props) => {
  const [isEditMode, setIsEditMode] = React.useState(true);

  const filters = useMemo(() => {
    if (props.filters && Object.keys(props.filters).length) {
      return props.filters;
    }
    return { conjunction: "and", groups: [] };
  }, [props.filters]);

  const [newRule, setNewRule] = React.useState(null);

  /**
   *
   * @type {(function(*, *): void)|*}
   */
  const updateGroup = useCallback(
    (groupId, group) => {
      const newFilters = { ...filters };

      if (!group.rules.length) {
        newFilters.groups.splice(groupId, 1);
        props.onChange(newFilters);
        return;
      }

      newFilters.groups[groupId] = group;
      props.onChange(newFilters);
    },
    [filters, props.onChange]
  );

  /**
   *
   * @type {(function(*): void)|*}
   */
  const updateMainConjunction = useCallback(
    (conjunction) => {
      const newFilters = { ...filters };
      newFilters.conjunction = conjunction;
      props.onChange(newFilters);
    },
    [filters]
  );

  /**
   *
   * @type {(function(): void)|*}
   */
  const clearNewRule = useCallback(() => {
    setNewRule(null);
  }, [newRule]);

  const editor = isEditMode ? (
    <AvailableRulesOptionsContext.Provider value={props.availableFilters}>
      <FiltersContext.Provider
        value={{
          filters,
          updateGroup,
          updateMainConjunction,
          newRule,
          clearNewRule,
          setNewRule,
        }}
      >
        <Editor />
      </FiltersContext.Provider>
    </AvailableRulesOptionsContext.Provider>
  ) : null;

  return <div>{editor}</div>;
};

export default Filters;
